import { useEffect, useRef, useState, type ReactNode } from 'react'

const SIDEBAR_ANIMATION_MS = 300

type AnimatedAgentBuilderSidebarProps = {
  open: boolean
  expanded: boolean
  children: ReactNode
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function AnimatedAgentBuilderSidebar({
  open,
  expanded,
  children,
}: AnimatedAgentBuilderSidebarProps) {
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const animationFrameRef = useRef<number | null>(null)
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (unmountTimerRef.current !== null) {
      clearTimeout(unmountTimerRef.current)
      unmountTimerRef.current = null
    }

    if (open) {
      if (!mounted) {
        setMounted(true)
        return
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        setVisible(true)
        animationFrameRef.current = null
      })
      return
    }

    setVisible(false)
    if (mounted) {
      unmountTimerRef.current = setTimeout(
        () => {
          setMounted(false)
          unmountTimerRef.current = null
        },
        prefersReducedMotion() ? 0 : SIDEBAR_ANIMATION_MS,
      )
    }

    return () => {
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
      if (unmountTimerRef.current !== null) clearTimeout(unmountTimerRef.current)
    }
  }, [mounted, open])

  useEffect(() => () => {
    if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
    if (unmountTimerRef.current !== null) clearTimeout(unmountTimerRef.current)
  }, [])

  if (!mounted) return null

  return (
    <aside
      aria-hidden={!visible}
      aria-label="Agent Builder chat"
      data-expanded={expanded}
      data-state={visible ? 'open' : 'closed'}
      data-testid="agent-builder-sidebar"
      {...(!visible ? { inert: '' } : {})}
      className={`fixed right-0 top-0 z-50 flex h-screen flex-col border-l border-gray-200 bg-white shadow-2xl transition-[width,transform] duration-300 ease-in-out motion-reduce:transition-none ${
        visible ? 'translate-x-0' : 'translate-x-full pointer-events-none'
      } ${expanded ? 'w-1/2' : 'w-[420px]'}`}
    >
      {children}
    </aside>
  )
}
